import { Box, Card, Typography } from '@mui/material';
import { cloneElement, useContext, useEffect, useMemo, useRef } from 'react';
import { BulkActionsToolbar, ExportButton, FilterButton, FilterContext, ListBase, ListToolbar, Pagination, Title, TopToolbar, sanitizeListRestProps, useListContext } from 'react-admin';
import { withPeriodDefaults } from '../../lib/normalizeDateRange';
import { CreateBulkSendButton, CreateDiscountButton, CustomExportButton, CustomGenericButton, PrintButton, PrintSummaryButton, ReportPrint } from '../index';

// "Remover todos os filtros" do react-admin faz setFilters({}) e apaga from/to; os relatórios
// com período obrigatório então mandam a query sem período e tomam 400. Reaplica o padrão da tela,
// mas SÓ quando a tela declara `periodRequired` (rota do backend com filter.from/to required):
// onde o período é opcional, o usuário precisa poder limpá-lo.
const PeriodDefaults = ({ defaults }) => {
  const { filterValues, displayedFilters, setFilters } = useListContext();

  useEffect(() => {
    const next = withPeriodDefaults(filterValues, defaults);
    if (next !== filterValues) setFilters(next, displayedFilters, false);
  }, [filterValues, defaults, displayedFilters, setFilters]);

  return null;
};

const ListShowActions = (props) => {
  const context = useListContext();

  const {
    filters: filtersProp,
    showFilterButton,
    maxResults,
    reportRef,
    reportSummaryRef,
    isReport,
    exporter,
    customExporter,
    exportFile,
    disablePrint,
    disableSummaryPrint = true,
    discountButton,
    bulkSendButton,
    ...restFilters
  } = props;

  const filters = useContext(FilterContext) || filtersProp;

  return useMemo(
    () => (
      <TopToolbar sx={{ alignItems: 'center' }} {...sanitizeListRestProps(restFilters)}>
        {
          showFilterButton && (filtersProp
            ? cloneElement(filtersProp, {
              resource: context.resource,
              showFilter: context.showFilter,
              displayedFilters: context.displayedFilters,
              filterValues: context.filterValues,
              context: 'button',
            })
            :
            filters && <FilterButton />)
        }

        {exporter &&
          <ExportButton resource={props.resource} exporter={exporter || undefined} maxResults={maxResults} />
        }
        {customExporter &&
          <CustomExportButton preExport={customExporter} isReport={isReport} maxResults={maxResults} />
        }
        {exportFile &&
          <CustomGenericButton onExport={exportFile} />
        }
        {!disablePrint &&
          <PrintButton printRef={reportRef} sx={{ alignSelf: 'center', height: '100%', verticalAlign: 'center' }} />
        }
        {!disableSummaryPrint &&
          <PrintSummaryButton reportSummaryRef={reportSummaryRef} sx={{ alignSelf: 'center', height: '100%', verticalAlign: 'center' }} />
        }
        {
          discountButton &&
          <CreateDiscountButton />
        }
        {
          bulkSendButton &&
          <CreateBulkSendButton />
        }
      </TopToolbar>
    ),
    [
      restFilters,
      filtersProp,
      filters,
      showFilterButton,
      maxResults,
      isReport,
      reportRef,
      exporter,
      customExporter,
      exportFile,
      disablePrint,
      discountButton,
    ]
  );
}

const ReportList = ({ maxResults = 50000, children, actions, bulkActionButtons,
  filters, title, pagination, exporter, disablePrint, landscape, titleOnPrint, exportFile,
  filterTablePrint, showFilterLabel = true, showPagination = true, printContent: PrintContent,
  noPadding = false, customExporter, discountButton, bulkSendButton, isReport, showFilterButton, disableSummaryPrint, periodRequired = false, ...props }) => {
  const reportRef = useRef();
  const reportSummaryRef = useRef();

  const ReportCard = () => (
    <Card>
      <BulkActionsToolbar>
        {bulkActionButtons}
      </BulkActionsToolbar>
      {children}
      {showPagination === true &&
        <Box sx={{
          '@media print': {
            display: 'none'
          }
        }}>
          {pagination ? pagination : <Pagination />}
        </Box>
      }
    </Card>
  );

  return (
    <ListBase {...props}>
      {periodRequired && <PeriodDefaults defaults={props.filterDefaultValues} />}
      <Title title={title} />
      <ListToolbar
        filters={filters}
        actions={actions ?
          cloneElement(actions, { reportRef, resource: props.resource })
          :
          <ListShowActions
            showFilterButton={showFilterButton}
            maxResults={maxResults}
            reportRef={reportRef}
            reportSummaryRef={reportSummaryRef}
            isReport={isReport}
            exporter={exporter}
            customExporter={customExporter}
            exportFile={exportFile}
            disablePrint={disablePrint}
            disableSummaryPrint={disableSummaryPrint}
            discountButton={discountButton}
            bulkSendButton={bulkSendButton}
          />
        }
      />
      {
        (PrintContent && !disableSummaryPrint) ?
          <>
            <ReportPrint noPadding={noPadding} ref={reportSummaryRef} title={titleOnPrint} landscape={landscape || false}>
              <Box sx={{
                display: 'none',
                '@media print': {
                  display: 'block'
                }
              }}>
                <PrintContent />
              </Box>
            </ReportPrint>
            <ReportPrint noPadding={noPadding} ref={reportRef} title={titleOnPrint} landscape={landscape || false}>
              {filterTablePrint &&
                <Box sx={{
                  display: 'none',
                  '@media print': {
                    display: 'block'
                  },
                  marginBottom: '10px'
                }}>
                  {showFilterLabel && <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>Filtros</Typography>}
                  <Card>
                    {filterTablePrint}
                  </Card>
                </Box>
              }
              <ReportCard />
            </ReportPrint>
          </>
          : PrintContent ?
            <>
              <ReportPrint noPadding={noPadding} ref={reportSummaryRef} title={titleOnPrint} landscape={landscape || false}>
                <PrintContent />
              </ReportPrint>
              <ReportCard />
            </>
            :
            <ReportPrint noPadding={noPadding} ref={reportRef} title={titleOnPrint} landscape={landscape || false}>
              {filterTablePrint &&
                <Box sx={{
                  display: 'none',
                  '@media print': {
                    display: 'block'
                  },
                  marginBottom: '10px'
                }}>
                  {showFilterLabel && <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>Filtros</Typography>}
                  <Card>
                    {filterTablePrint}
                  </Card>
                </Box>
              }
              <ReportCard />
            </ReportPrint>
      }
    </ListBase>
  );
};

export default ReportList;
